import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { SchoolYearProvider } from "./context/SchoolYearContext";
import IntroScreen from "./components/screens/IntroScreen";
import LoginScreen from "./components/screens/LoginScreen";
import DashboardScreen from "./components/screens/DashboardScreen";
import ExpensesScreen from "./components/screens/ExpensesScreen";
import CollectionScreen from "./components/screens/CollectionScreen";
import CollectionEditScreen from "./components/screens/CollectionEditScreen";
import PlannedScreen from "./components/screens/PlannedScreen";
import NoticesScreen from "./components/screens/NoticesScreen";
import AdminScreen from "./components/screens/AdminScreen";
import ExpenseDocumentsScreen from "./components/screens/ExpenseDocumentsScreen";
import SettingsScreen from "./components/screens/SettingsScreen";
import PolitykaPrywatnosci from "./components/screens/PolitykaPrywatnosci";
import AppLayout from "./components/layout/AppLayout";
import CookieBanner from "./components/ui/CookieBanner";
import SchoolYearSwitcher from "./components/school-years/SchoolYearSwitcher";

function FullScreenLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#f6fbfa]">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-emerald-400 border-t-transparent" />
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) return <FullScreenLoader />;
  if (!user) return <Navigate to="/" replace />;

  return children;
}

function AdminRoute({ children }) {
  const { isAdmin, loading } = useAuth();

  if (loading) return <FullScreenLoader />;
  if (!isAdmin) return <Navigate to="/dashboard" replace />;

  return children;
}

function ProtectedPage({ children }) {
  return (
    <ProtectedRoute>
      <AppLayout>
        <SchoolYearSwitcher />
        {children}
      </AppLayout>
    </ProtectedRoute>
  );
}

function AppRoutes() {
  const { user, loading } = useAuth();

  if (loading) return <FullScreenLoader />;

  return (
    <Routes>
      <Route
        path="/"
        element={user ? <Navigate to="/dashboard" replace /> : <IntroScreen />}
      />

      <Route
        path="/login"
        element={user ? <Navigate to="/dashboard" replace /> : <LoginScreen />}
      />

      <Route path="/privacy" element={<PolitykaPrywatnosci />} />

      <Route
        path="/dashboard"
        element={
          <ProtectedPage>
            <DashboardScreen />
          </ProtectedPage>
        }
      />

      <Route
        path="/expenses"
        element={
          <ProtectedPage>
            <ExpensesScreen />
          </ProtectedPage>
        }
      />

      <Route
        path="/collection"
        element={
          <ProtectedPage>
            <CollectionScreen />
          </ProtectedPage>
        }
      />

      <Route
        path="/collection/edit/:id"
        element={
          <ProtectedPage>
            <CollectionEditScreen />
          </ProtectedPage>
        }
      />

      <Route
        path="/planned"
        element={
          <ProtectedPage>
            <PlannedScreen />
          </ProtectedPage>
        }
      />

      <Route
        path="/notices"
        element={
          <ProtectedPage>
            <NoticesScreen />
          </ProtectedPage>
        }
      />

      <Route
        path="/settings"
        element={
          <ProtectedPage>
            <SettingsScreen />
          </ProtectedPage>
        }
      />

      <Route
        path="/admin/documents"
        element={
          <ProtectedRoute>
            <AdminRoute>
              <AppLayout>
                <SchoolYearSwitcher />
                <ExpenseDocumentsScreen />
              </AppLayout>
            </AdminRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminRoute>
              <AppLayout>
                <SchoolYearSwitcher />
                <AdminScreen />
              </AppLayout>
            </AdminRoute>
          </ProtectedRoute>
        }
      />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <SchoolYearProvider>
          <AppRoutes />
          <CookieBanner />
          <Toaster
            position="top-right"
            toastOptions={{
              style: {
                background: "rgba(255,255,255,0.92)",
                color: "#334155",
                border: "1px solid rgba(255,255,255,0.7)",
                borderRadius: "16px",
                fontFamily: "'DM Sans', sans-serif",
                boxShadow: "0 18px 48px rgba(15,23,42,0.10)",
                backdropFilter: "blur(10px)",
              },
              success: {
                iconTheme: {
                  primary: "#10b981",
                  secondary: "#ffffff",
                },
              },
              error: {
                iconTheme: {
                  primary: "#ef4444",
                  secondary: "#ffffff",
                },
              },
            }}
          />
        </SchoolYearProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
